/**
 * نقشه‌ی مسیرها. برابریِ کامل با اپ در فاز ۴ ساخته می‌شه — نگاه کن به جدولِ
 * «parity map» در plan/web-platform.md بخشِ ۲. الان فقط سه مسیر داریم.
 */
import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/core/session';
import { needsTermsAcceptance } from '@/core/legal/terms';
import { isSafeDest, rememberAfterAuth, takeAfterAuth } from '@/core/lib/afterAuth';
import { trackScreen } from '@/core/lib/analytics';
import { markNav } from '@/core/lib/devlog';
import { LoginPage } from '@/features/auth/LoginPage';
import { CreateProfilePage } from '@/features/auth/CreateProfilePage';
import type { AuthReturn } from '@/features/auth/returnHere';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { HomePage } from '@/features/home/HomePage';
import { LegalPage } from '@/features/legal/LegalPage';
import { AcceptTermsPage } from '@/features/legal/AcceptTermsPage';
import { GetAppPage } from '@/features/getapp/GetAppPage';
import { CreateSignalPage } from '@/features/signals/CreateSignalPage';
import { SignalPage } from '@/features/signals/SignalPage';
import { EditSignalPage } from '@/features/signals/EditSignalPage';
import { SignalVersionsPage } from '@/features/signals/SignalVersionsPage';
import { PublicSignalPage } from '@/features/signals/PublicSignalPage';
import { InvitePage } from '@/features/signals/InvitePage';
import { BlockSenderPage } from '@/features/signals/BlockSenderPage';
import { DiscoverPage } from '@/features/discover/DiscoverPage';
import { ItemPage } from '@/features/discover/ItemPage';
import { MyEventEditPage } from '@/features/discover/MyEventEditPage';
import { FriendsPage } from '@/features/friends/FriendsPage';
import { PlansPage } from '@/features/plans/PlansPage';
import { NotificationSettingsPage } from '@/features/settings/NotificationSettingsPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { FollowedTagsPage } from '@/features/settings/FollowedTagsPage';
import { BlockedUsersPage } from '@/features/settings/BlockedUsersPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ChangePasswordPage } from '@/features/profile/ChangePasswordPage';
import { VersionPage } from '@/features/version/VersionPage';
import { AvailabilityPage } from '@/features/profile/AvailabilityPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { CityPage } from '@/features/settings/CityPage';
import { FeedbackPage } from '@/features/settings/FeedbackPage';
import { SuggestPage } from '@/features/settings/SuggestPage';
import { DeleteAccountPage } from '@/features/settings/DeleteAccountPage';

/**
 * صفحه‌های داخلی فقط با نشست. `loading` مهمه: بدونش، در همون تیکِ اولِ لود —
 * که هنوز نشست از localStorage خونده نشده — کاربرِ واردشده یه پرشِ کوتاه به
 * صفحه‌ی ورود می‌بینه.
 *
 * آدرسی که کاربر می‌خواست توی `state.from` همراهش می‌ره تا بعدِ ورود همون‌جا
 * برگرده، نه به صفحه‌ی اول. حالا که کشف عمومی شده این واقعاً پیش می‌آد:
 * مهمان از یک لینکِ مستقیمِ `/friends` می‌رسه، وارد می‌شه، و باید دوستانش رو
 * ببینه نه فهرستِ رخدادها.
 *
 * `returnHere={false}` برای صفحه‌ای که برگشتن به آن بعد از ورود بی‌معناست:
 * کسی که در «تغییر رمز» خارج شد و دوباره وارد شد، نمی‌خواهد دوباره فرمِ
 * رمز را ببیند — به صفحه‌ی اول (کشف) می‌رود (WHOSUP-367).
 */
function Private({ children, returnHere = true }: { children: React.ReactNode; returnHere?: boolean }) {
  const { session, profile, profileChecked, loading } = useSession();
  const location = useLocation();
  if (loading) return null;
  if (!session) {
    return returnHere
      ? <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />
      : <Navigate to="/auth" replace />;
  }
  /**
   * نشست هست ولی نمایه نه.
   *
   * تا امروز این حالت روی وب **بن‌بست** بود: ورود (به‌ویژه با گوگل) حساب
   * می‌ساخت و همین‌جا رهاش می‌کرد — بی‌اسم، بی‌نامِ کاربری، و از مایگریشن
   * 0073 بی‌کلیدِ تماس، یعنی نامرئی برای دفترچه‌ی دوستان. هر صفحه‌ی پشتِ
   * ورود چیزی برای نشون دادن نداشت و فقط یه یادداشتِ «یه قدم مونده» می‌داد
   * که به هیچ‌جا نمی‌رفت.
   *
   * حالا همون یه قدم واقعاً برداشته می‌شه، جای هر صفحه‌ای که خواسته شده.
   *
   * ⚠️ `profileChecked` شرطِ لازمه، نه احتیاطِ اضافی: نشست یه پلک زودتر از
   * پروفایل می‌رسه (onAuthStateChange اول `session` رو می‌ذاره و بعد
   * پروفایل رو می‌خونه). بدونِ این شرط، همون یه پلک کافیه که کاربرِ کاملاً
   * عادی — درست بعد از ورود — فرمِ «یه قدم مونده» رو ببینه.
   */
  if (!profile) return profileChecked ? <CreateProfilePage /> : null;
  /**
   * سند عوض شده و این آدم هنوز نسخه‌ی تازه را نپذیرفته.
   *
   * دقیقاً همان‌جای `CreateProfilePage` می‌نشیند و به همان دلیل: چیزی که
   * قرار است پیش از ادامه‌ی کار گرفته شود، جای هر صفحه‌ای که خواسته شده
   * نشان داده می‌شود، نه با یک ناوبریِ اضافه که کاربر بتواند دورش بزند.
   */
  if (needsTermsAcceptance(profile.termsVersion)) return <AcceptTermsPage />;
  return <>{children}</>;
}

/**
 * صفحه‌ی فرود: برای مهمان کشف، برای حسابِ بی‌نمایه همان «یه قدم مونده».
 *
 * ---------- شکافی که این پر می‌کنه ----------
 * `Private` این حالت را برای **هر صفحه‌ی پشتِ ورود** می‌گیرد، ولی `/` عمداً
 * بیرونِ `Private` است تا مهمان هم چیزی ببیند — و `AuthRoute` بعد از ورودِ
 * موفق دقیقاً به همین `/` می‌فرستد. نتیجه‌اش این بود: کسی که همین الان
 * ثبت‌نام کرده روی کشف می‌نشست و **هیچ‌وقت ازش نمایه خواسته نمی‌شد**. نه
 * بن‌بستِ آشکار، که بدترش: صفحه‌ای که کار می‌کند و چیزی کم به نظر نمی‌رسد،
 * در حالی که حساب بی‌اسم و بی‌نامِ کاربری مانده و از مایگریشن 0073 کلیدِ
 * تماسش هم ثبت نشده — یعنی نامرئی برای دفترچه‌ی دوستانش.
 *
 * همان اتفاقی که سربرگِ `CreateProfilePage` توصیفش می‌کند («دو حسابِ گوگل
 * روی دیتابیسِ واقعی همین‌طور رها شده بودند»)، این بار از راهِ صفحه‌ی فرود.
 * تستِ E2E هم نگرفته بودش چون صریحاً `/signals` را باز می‌کرد نه `/`.
 *
 * ---------- چرا فقط همین یک مسیر ----------
 * بقیه‌ی صفحه‌های عمومی هرکدام دلیلِ روشنی دارند که بدونِ حساب کار کنند و
 * نباید پشتِ این فرم بروند: `/legal` همان شرایطی است که این فرم می‌خواهد
 * پذیرفته شود، و `/s/:token`، `/i/:token` و `/get` اصلاً برای کسی ساخته
 * شده‌اند که حساب ندارد. `/` تنها مسیرِ عمومی است که «خانه‌ی کاربرِ
 * واردشده» هم هست، پس تنها جایی است که این شکاف را داشت.
 *
 * ⚠️ `profileChecked` این‌جا هم شرطِ لازم است، به همان دلیلِ `Private`:
 * نشست یک پلک زودتر از پروفایل می‌رسد، و بدونش هر کاربرِ عادی درست بعد از
 * ورود یک لحظه فرمِ «یه قدم مونده» را می‌دید. تا وقتی جواب معلوم نشده،
 * چیزی که نشان داده می‌شود همان صفحه‌ی عمومی است — نه صفحه‌ی سفید، چون
 * این صفحه‌ی اولِ سایت است و بیشترین بیننده‌اش کسی است که اصلاً حساب ندارد.
 */
function Landing() {
  const { session, profile, profileChecked } = useSession();
  if (session && profileChecked && !profile) return <CreateProfilePage />;
  // و همان شکاف برای پذیرشِ مجدد: `/` بیرونِ `Private` است، پس کاربرِ
  // واردشده‌ای که صفحه‌ی اول را باز می‌کند بدونِ این هیچ‌وقت سندِ تازه را
  // نمی‌دید.
  if (profile && needsTermsAcceptance(profile.termsVersion)) return <AcceptTermsPage />;
  return <DiscoverPage />;
}

/** مقصدِ بعد از ورود، فقط اگر واقعاً یک مسیرِ داخلی باشد — نگاه کن به `isSafeDest`. */
const safeFrom = (raw: string | null | undefined): string | null =>
  (isSafeDest(raw) ? raw : null);

/** «از کجا آمدم» — از `state` (کلیکِ داخلِ اپ) یا از `?from=` در خودِ آدرس. */
const fromOf = (location: { state: unknown; search: string }): string | null =>
  safeFrom((location.state as { from?: string } | null)?.from)
  ?? safeFrom(new URLSearchParams(location.search).get('from'));

/**
 * صفحه‌ی ورود. کاربرِ واردشده اینجا کاری نداره — به جایی که ازش اومده
 * برمی‌گرده (یا صفحه‌ی اول، اگه مستقیم اومده باشه).
 *
 * دو راه برای گفتنِ «از کجا آمدم»: `state` (کلیکِ داخلِ اپ، از `Private`) و
 * `?from=` در خودِ آدرس. دومی برای لینکی است که **بیرونِ اپ** نوشته می‌شود و
 * باید با کپی/پیست هم کار کند — مثلِ لینکِ «برای حذف وارد شو» در نسخه‌ی
 * عمومیِ صفحه‌ی حذفِ حساب (WHOSUP-169).
 *
 * ⚠ هیچ‌کدامِ این دو از **ورود با گوگل/اپل** جان به در نمی‌برد: آن‌جا مرورگر
 * کلاً از سایت می‌رود و به آدرسِ ثابتِ `redirectTo` برمی‌گردد، پس نه `state`
 * می‌ماند و نه `?from=`. برای همین مقصد این‌جا روی دستگاه هم نوشته می‌شود
 * (`rememberAfterAuth`) و `useAfterAuthRedirect` بعدِ برگشت مصرفش می‌کند.
 */
function AuthRoute() {
  const { session, loading } = useSession();
  const location = useLocation();
  const from = fromOf(location);
  const back = !!from && (location.state as AuthReturn | null)?.back === true;

  // فقط تا وقتی فرمِ ورود روی صفحه است؛ بعد از ورود دیگر چیزی برای سپردن نیست.
  useEffect(() => {
    if (!loading && !session && from) void rememberAfterAuth(from);
  }, [loading, session, from]);

  if (loading) return null;
  if (session) return back ? <GoBack fallback={from} /> : <Navigate to={from ?? '/'} replace />;
  return <LoginPage dest={from} />;
}

/**
 * «ورود» روی همین صفحه باز شده بود (`returnHere`)، پس همان صفحه درست زیرِ آن
 * در تاریخچه است: یک قدم عقب، نه `replace` — WHOSUP-359. `replace` یک نسخه‌ی
 * دیگر از همان صفحه را جای «ورود» می‌گذاشت و دکمه‌ی برگشتِ مرورگر اول همان
 * صفحه را دوباره نشان می‌داد.
 *
 * `idx` شماره‌ی این مدخل در تاریخچه است که خودِ react-router در
 * `history.state` می‌نویسد. صفر یعنی چیزی زیرش نیست (مثلاً «ورود» در تبِ
 * تازه باز شده) — آن‌وقت همان `replace` قدیمی.
 */
function GoBack({ fallback }: { fallback: string }) {
  const nav = useNavigate();
  // StrictMode اثر را دو بار اجرا می‌کند؛ دو قدم عقب یعنی صفحه‌ی قبل از آن.
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) nav(-1);
    else nav(fallback, { replace: true });
  }, []);
  return null;
}

/**
 * برگرداندنِ کاربر به همان‌جایی که قبلِ ورود می‌خواست — مخصوصِ ورودی که از
 * سایت بیرون رفته و برگشته (گوگل/اپل، و لینکِ تاییدِ ایمیل).
 *
 * آن مسیرها همیشه روی `redirectTo`ِ ثابت فرود می‌آیند — یعنی `/` — و
 * `AuthRoute` اصلاً رندر نمی‌شود که بتواند کاری بکند. پس مصرفِ مقصدِ
 * ذخیره‌شده این‌جاست، یک لایه بالاتر از مسیرها.
 *
 * ⚠ شرطِ `profile`: حسابی که هنوز نمایه ندارد باید اول همان یک قدم را
 * بردارد (`Landing` فرمش را نشان می‌دهد). پرتاب کردنش وسطِ آن قدم به یک
 * صفحه‌ی داخلی یعنی همان بن‌بستی که `CreateProfilePage` برای رفعش ساخته شد.
 * مقصد دست‌نخورده می‌ماند و به‌محضِ ساخته شدنِ نمایه همین اثر دوباره می‌دود.
 *
 * ⚠ فقط روی `/` ناوبری می‌کند (WHOSUP-352). مقصدِ ذخیره‌شده یک کلید برای
 * همه‌ی تب‌هاست: تبِ دیگری که همان لحظه روی `/plans` بود، با خروجِ «تاییدِ
 * حذفِ حساب» به `/auth` پرت می‌شد و `/plans` را روی مقصدِ صفحه‌ی حذف
 * می‌نوشت — و بعد از ورودِ دوباره، صفحه‌ی حذف یک پلک دیده می‌شد و جایش
 * `/plans` می‌آمد. ورودی که از سایت بیرون نرفته، با `?from=`/`state` خودش
 * به مقصد رسیده؛ این‌جا فقط مصرف می‌شود تا برای ورودِ بعدی نماند.
 */
function useAfterAuthRedirect(): void {
  const { session, profile, profileChecked } = useSession();
  const location = useLocation();
  const nav = useNavigate();
  // تازه‌ترین آدرس، نه آدرسِ لحظه‌ای که اثر ساخته شد — `takeAfterAuth` ناهمگام است.
  const here = useRef(location);
  here.current = location;
  useEffect(() => {
    if (!session || !profileChecked || !profile) return;
    let alive = true;
    void takeAfterAuth().then((dest) => {
      if (!alive || !dest) return;
      const { pathname, search } = here.current;
      // اگر همین حالا همان‌جاییم، فقط مصرفش کن و ناوبریِ بی‌خود نکن.
      if (pathname === '/' && dest !== pathname + search) nav(dest, { replace: true });
    });
    return () => { alive = false; };
  }, [session, profile, profileChecked]);
}

/**
 * هر تعویضِ مسیر یک `screen_view`.
 *
 * ⚠️ **الگوی مسیر** ثبت می‌شود نه آدرسِ واقعی: `/signal/:id` و نه
 * `/signal/9f3c…`. آدرسِ واقعی شناسه‌ی یک سیگنالِ مشخص است و آن یعنی
 * ردیابیِ اینکه چه کسی چه چیزی را دید — دقیقاً همان چیزی که در
 * lib/analytics قول دادیم ثبت نکنیم.
 */
function useScreenTracking(): void {
  const location = useLocation();
  useEffect(() => {
    const screen = location.pathname
      .replace(/\/(s|i)\/[^/]+/, '/$1/:token')
      .replace(/\/(signal|event|place|admin\/item)\/[^/]+/, '/$1/:id');
    /**
     * ⚠ `trackScreen` و نه `track('screen_view')`: آن یکی فقط نامِ صفحه را
     * می‌فرستد، این یکی «از کجا آمد» و «با کدام دکمه» را هم می‌گذارد —
     * سه‌تایی که سفرِ کاربر را می‌سازند. نامِ صفحه هم همان‌جا نرمال می‌شود،
     * پس وب و موبایل یک واژگان دارند.
     */
    trackScreen(screen);
    /**
     * و همان صفحه با زمانش، توی لاگِ فعالیتِ توسعه — قرینه‌ی `ScreenTracker`
     * در اپ. دو مقصدِ جدا برای یک اتفاق، عمداً: بالایی رویدادِ محصول است و
     * می‌ماند، پایینی ابزارِ توسعه است و با هر مرج پاک می‌شود.
     */
    markNav(screen);

    /**
     * تگِ canonical را با مسیرِ جاری هم‌خوان نگه می‌دارد.
     *
     * index.html یک canonicalِ ثابت به صفحه‌ی اول دارد (توضیحش همان‌جاست) و
     * آن برای مهم‌ترین صفحه کافی است. ولی این یک SPA است: مسیر بدونِ بارگذاریِ
     * دوباره عوض می‌شود و آن تگ همان‌جا می‌ماند. یعنی گوگل برای هر صفحه‌ای
     * می‌خواند «اصلش صفحه‌ی اول است» — که برای صفحه‌ی یک رخداد غلط است و آن
     * صفحه را از فهرست بیرون می‌اندازد.
     *
     * دامنه همیشه outami.com است حتی وقتی کاربر روی palsandplans.com یا
     * whosup.ca باشد — کلِ هدفِ این تگ همین است: آن دو عمداً زنده مانده‌اند و
     * باید اعتبارشان به دامنه‌ی اصلی برود، نه اینکه رقیبش شوند.
     */
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `https://outami.com${location.pathname}`;
  }, [location.pathname]);
}

export function AppRoutes() {
  useScreenTracking();
  useAfterAuthRedirect();
  return (
    <Routes>
      {/* دیسکاور صفحه‌ی اصلیه — نه یه‌ تبِ کنارِ بقیه. فیدِ سیگنال‌ها از
          راهِ نوارِ کناری (هاور) دیده می‌شه؛ `/signals` فقط برای لینکِ مستقیمه.

          عمداً بیرونِ Private: صفحه‌ی اولِ سایت باید بدونِ حساب هم چیزی برای
          نشون دادن داشته باشه. چیزی که مهمان می‌بینه دقیقاً همونیه که
          دیتابیس بهش می‌ده — رخداد و مکانِ «منتشرشده» و دسته‌های داخلی
          (مایگریشن 0062). دوستان، سیگنال‌ها و پروفایل همچنان پشتِ ورودن.

          ولی «بیرونِ Private» یعنی بیرونِ نگهبانِ ورود، نه بیرونِ همه چیز:
          حسابی که نمایه ندارد این‌جا هم باید همان یه قدم را بردارد — نگاه
          کن به `Landing`. */}
      <Route path="/" element={<Landing />} />
      {/* شماره‌ی انتشار برای تسترِ دستی، بی ورود — features/version/VersionPage */}
      <Route path="/version" element={<VersionPage />} />
      <Route path="/signals" element={<Private><HomePage /></Private>} />
      <Route path="/plans" element={<Private><PlansPage /></Private>} />
      {/* ترتیب مهمه: `new` باید قبل از `:id` بیاد وگرنه به‌عنوان شناسه خونده می‌شه. */}
      <Route path="/signal/new" element={<Private><CreateSignalPage /></Private>} />
      <Route path="/signal/:id" element={<Private><SignalPage /></Private>} />
      {/* ویرایش و تاریخچه زیرِ خودِ سیگنال، نه مسیرِ جدا: هر دو درباره‌ی
          همون شناسه‌ان و آدرسشون باید همینو بگه. */}
      <Route path="/signal/:id/edit" element={<Private><EditSignalPage /></Private>} />
      <Route path="/signal/:id/versions" element={<Private><SignalVersionsPage /></Private>} />
      <Route path="/discover" element={<DiscoverPage />} />
      {/* فهرستِ عمومی که کارت‌هاش به صفحه‌ی ورود می‌خورن یعنی هیچی — پس
          صفحه‌ی خودِ رخداد و مکان هم عمومیه. */}
      {/* ترتیب مهمه: `edit` زیرِ همون شناسه‌ست، پس قبل از هر مسیرِ عام‌تری
          می‌آد. فقط صاحبِ خودِ رخداد می‌بیندش — نگاه کن به MyEventEditPage. */}
      <Route path="/event/:id/edit" element={<Private><MyEventEditPage /></Private>} />
      <Route path="/event/:id" element={<ItemPage kind="event" />} />
      <Route path="/place/:id" element={<ItemPage kind="place" />} />
      <Route path="/friends" element={<Private><FriendsPage /></Private>} />
      <Route path="/settings/notifications"
        element={<Private><NotificationSettingsPage /></Private>} />
      {/* تاریخچه‌ی همه‌ی اعلان‌ها (WHOSUP-131) — راهش از صفحه‌ی پروفایله. */}
      <Route path="/notifications" element={<Private><NotificationsPage /></Private>} />
      {/* آدرسِ این صفحه توی ایمیلِ تگِ دنبال‌شده هم چاپ می‌شه
          (supabase/functions/send-tag-email) — عوض کردنش یعنی لینکِ مرده. */}
      <Route path="/settings/followed-tags"
        element={<Private><FollowedTagsPage /></Private>} />
      {/* تنها جایی که می‌شود مسدودیتی را برداشت: خودِ آن نفر از همه‌جای دیگر
          ناپیداست، پس دکمه‌ای کنارِ اسمش نمانده که بشود زد (WHOSUP-230). */}
      <Route path="/settings/blocked"
        element={<Private><BlockedUsersPage /></Private>} />
      <Route path="/profile" element={<Private><ProfilePage /></Private>} />
      <Route path="/profile/password" element={<Private returnHere={false}><ChangePasswordPage /></Private>} />
      <Route path="/availability" element={<Private><AvailabilityPage /></Private>} />
      <Route path="/settings" element={<Private><SettingsPage /></Private>} />
      <Route path="/settings/city" element={<Private><CityPage /></Private>} />
      <Route path="/feedback" element={<Private><FeedbackPage /></Private>} />
      <Route path="/suggest" element={<Private><SuggestPage /></Private>} />
      {/* عمداً بیرونِ Private (WHOSUP-169): این همان آدرسی است که در کنسولِ
          گوگل‌پلی به‌عنوانِ «Delete app account» ثبت شده، و گوگل شرط می‌کند
          بدونِ ورود دیده شود. پشتِ گارد که بود، بازبین به /auth پرت می‌شد و
          فرمِ ورود می‌دید نه توضیحِ حذف.

          گارد نرفته، فقط جابه‌جا شده: خودِ صفحه برای مهمان توضیح نشان می‌دهد
          و برای واردشده ابزارِ حذف. هیچ عملی بدونِ نشست انجام نمی‌شود. */}
      <Route path="/settings/delete-account" element={<DeleteAccountPage />} />
      {/* ابزارِ بازبینی به اپِ ادمین رفت (`admin.outami.com`، سربرگِ «صفِ
          بازبینی»). این‌جا مسیری نمی‌ماند: دو جا برای یک کار یعنی یکی‌شان
          کهنه می‌شود، و بسته‌ی کاربرِ عادی هم نباید کدِ ادمین را حمل کند. */}
      <Route path="/auth" element={<AuthRoute />} />
      {/* عمداً بیرونِ Private: لینکِ بازیابی نشست می‌سازد، ولی اگر منقضی شده
          باشد نشستی در کار نیست و کاربر باید توضیح ببیند نه صفحه‌ی ورود. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* عمداً بیرونِ Private: گیرنده‌ی لینک نه حساب داره نه اپ. اگه پشتِ
          ورود می‌رفت، کلِ هدفِ این صفحه از بین می‌رفت. */}
      <Route path="/s/:token" element={<PublicSignalPage />} />
      {/* لینکِ شخصیِ توی ایمیلِ دعوت (مایگریشن 0066). همان دلیلِ بالا، به‌علاوه
          اینکه توکنش هویتِ طرف است — فرستادنش به صفحه‌ی ورود یعنی دور ریختنش. */}
      <Route path="/i/:token" element={<InvitePage />} />
      {/* لینکِ پایینِ ایمیل؛ عمومی است چون گیرنده ممکن است حساب نداشته باشد. */}
      <Route path="/block-sender/:token" element={<BlockSenderPage />} />
      <Route path="/legal" element={<LegalPage />} />
      {/* مقصدِ QRِ «اپ رو بگیر». عمداً بیرونِ Private و عمداً یک صفحه‌ی واسط:
          یک QR نمی‌داند چه کسی اسکنش می‌کند، پس تصمیمِ «گوگل‌پلی یا اپ‌استور»
          روی همان دستگاه گرفته می‌شود. */}
      <Route path="/get" element={<GetAppPage />} />
      {/* آدرسِ ناشناخته کاربر را در بن‌بست نمی‌ذاره. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
